import { useEffect, useRef } from "react";
import Container from "@@/elements/Container";
import OptimizelyBlock, { OptimizelyBlockProps } from "@@/integrations/optimizly/Block";
import EpiProperty from "@@/integrations/optimizly/Property";
import styles from "./index.module.scss";

type ContentScriptProps = {
  script?: string;
  anchorId?: string;
};

const ContentScriptContent = ({ script, anchorId }: ContentScriptProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!script || !containerRef.current) return;

    const container = containerRef.current;
    const scriptRegex = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
    const scripts: string[] = script.match(scriptRegex) || [];

    scripts.forEach((scriptTag) => {
      const srcMatch = scriptTag.match(/src\s*=\s*["'](.*?)["']/);
      const idMatch = scriptTag.match(/id\s*=\s*["'](.*?)["']/);

      if (srcMatch) {
        const existing = document.querySelector(`script[src="${srcMatch[1]}"]`);
        if (existing) return;

        const el = document.createElement("script");
        el.src = srcMatch[1];
        el.type = "text/javascript";
        el.async = true;
        if (idMatch) el.id = idMatch[1];
        container.appendChild(el);
      }
    });
  }, [script]);

  return (
    <Container id={anchorId}>
      <EpiProperty<ContentScriptProps> name="script">
        <div ref={containerRef} className={styles.content_script} />
      </EpiProperty>
    </Container>
  );
};

export type ProvidedContentScriptProps =
  OptimizelyBlockProps<ContentScriptProps> & ContentScriptProps;

const ContentScript = ({
  blockId,
  inEditMode,
  properties,
  anchorId,
  ...rest
}: ProvidedContentScriptProps) => (
  <OptimizelyBlock
    blockId={blockId}
    inEditMode={inEditMode}
    properties={properties}
    anchorId={anchorId}
  >
    {(optimizlyProperties) => (
      <ContentScriptContent anchorId={anchorId} {...rest} {...optimizlyProperties} />
    )}
  </OptimizelyBlock>
);

export default ContentScript;
